import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseDataService } from './base.data.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import {
  IGrabRouteRecommendationResult,
  IRouteRecommendation,
  IRouteRecommendationResult,
  IRouteRecommendationSearch,
} from '../interfaces/route-recommendations/route-recommendation';
import {
  IRouteHiddenReason,
  ISetRouteHidden,
} from '../interfaces/route-recommendations/route-rejection-reason';
import { UpdateTaskScheduleDTO } from '../components/task-supplier-schedule/task-supplier-schedule';
import { IRouteRecommendationUserPreferenceChangeLimitDetails } from '../interfaces/route-recommendations/route-recommendation-user-preference-change-limit-detail';
import { IRouteRecommendationItinerary } from '../interfaces/route-recommendations/route-recommendation-itinerary';
import { IRouteRecommendationAlternateDate } from '../interfaces/route-recommendations/route-recommendation-alternate-date';
import { IRouteGrabbedJob } from '../interfaces/route-recommendations/route-grabbed';
import { IRouteExtenderFilterOption } from '../interfaces/route-recommendations/route-extender-filter-option';
import { IRouteJobScreenTasks } from '../interfaces/screen-specific/job-screen/route-details/route-job-screen-tasks';
import { IRouteJobScreenTaskDetail } from '../interfaces/screen-specific/job-screen/route-details/route-job-screen-task-detail';
import { OrganisationSettingEnum } from '../enums/organisation-settings';
import { UserService } from './user.service';
import { OrganisationSettingService } from './organisation-setting.service';

@Injectable({ providedIn: 'root' })
export class RouteRecommendationService extends BaseDataService {
  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private userService: UserService,
    private organisationSettingsService: OrganisationSettingService,
  ) {
    super(http, handlersFactory);
  }

  public url = '/api/route-recommendations';

  public search(request: IRouteRecommendationSearch): Promise<IRouteRecommendationResult> {
    let params = this.constructHttpParams(request);

    return this.http.get<IRouteRecommendationResult>(this.url, { params: params }).toPromise();
  }

  public get(id: number): Promise<IRouteRecommendation> {
    return this.http.get<IRouteRecommendation>(`${this.url}/${id}`).toPromise();
  }

  async grabRoute(id: number, taskSupplierSchedules: UpdateTaskScheduleDTO[]) {
    const url = `${this.url}/${id}/grab`;

    const result = await this.http
      .put<IGrabRouteRecommendationResult>(url, {
        routeRecommendationId: id,
        taskSupplierSchedules,
      })
      .toPromise();

    if (result.success) {
      this.grabbedEvent.emit(id);
    }

    return result;
  }

  async deployRoute(id: number, taskSupplierSchedules: UpdateTaskScheduleDTO[]) {
    const url = `${this.url}/${id}/deploy`;

    const result = await this.http
      .put<IGrabRouteRecommendationResult>(url, {
        routeRecommendationId: id,
        taskSupplierSchedules,
      })
      .toPromise();

    if (result.success) {
      this.grabbedEvent.emit(id);
    }

    return result;
  }

  async setHidden(dto: ISetRouteHidden) {
    const newUrl = this.url + '/' + dto.routeRecommendationId + '/hidden';
    await this.http.put<IRouteRecommendation>(newUrl, dto).toPromise();
    if (dto.hidden) {
      this.hiddenEvent.emit(dto.routeRecommendationId);
    } else {
      this.unHiddenEvent.emit(dto.routeRecommendationId);
    }
  }

  async setWatched(id: number, isWatched: boolean) {
    const newUrl = this.url + '/' + id + '/watched';
    await this.http.put(newUrl, { watched: isWatched }).toPromise();
    if (isWatched) {
      this.watchedEvent.emit(id);
    } else {
      this.unWatchedEvent.emit(id);
    }
  }

  searchRouteHiddenReasons(params: {}) {
    const url = '/api/route-hide-reasons';

    return this.http.get<IRouteHiddenReason[]>(url, { params: params }).toPromise();
  }

  getTaskScope() {
    const requestUrl = `${this.url}/task-scope-length`;
    return this.http.get<{ lengthInDays: number }>(requestUrl).toPromise();
  }

  isProcessingNewSupplierRoutes(): Promise<boolean> {
    return this.http.get<boolean>(this.url + '/isProcessingNewSupplierRoutes').toPromise();
  }

  public getItinerary(id: number, accessToken: string): Promise<IRouteRecommendationItinerary> {
    return this.http
      .get<IRouteRecommendationItinerary>(
        `${this.url}/itinerary/${id}?accessToken=${encodeURIComponent(accessToken)}`,
      )
      .toPromise();
  }

  public getItineraryAlternateDates(id: number): Promise<IRouteRecommendationAlternateDate[]> {
    return this.http
      .get<IRouteRecommendationAlternateDate[]>(`${this.url}/itinerary/${id}/alternateDates`)
      .toPromise();
  }

  public getRouteGrabbedConfirmation(id: number): Promise<IRouteGrabbedJob> {
    return this.http
      .get<IRouteGrabbedJob>(`${this.url}/routeGrabbedConfirmation/${id}`)
      .toPromise();
  }

  public getExtenderFilterOptions(): Promise<IRouteExtenderFilterOption[]> {
    return this.http
      .get<IRouteExtenderFilterOption[]>(`${this.url}/extender/filterOptions`)
      .toPromise();
  }

  public getRouteJobScreenTasks(id: number): Promise<IRouteJobScreenTasks> {
    return this.http
      .get<IRouteJobScreenTasks>(`${this.url}/route-job-screen/${id}/tasks`)
      .toPromise();
  }

  public getRouteJobScreenTaskDetails(id: number): Promise<IRouteJobScreenTaskDetail> {
    return this.http
      .get<IRouteJobScreenTaskDetail>(`${this.url}/route-job-screen/${id}/tasks/detail`)
      .toPromise();
  }

  public getSupplierDropdownItems(): Promise<any> {
    return this.http.get<any>(`${this.url}/suppliers/filterOptions`).toPromise();
  }

  public async isRouteSearchEnabled(): Promise<boolean> {
    const organisation = await this.userService.getCurrentUserOrg();
    const organisationSettings = await this.organisationSettingsService.search({
      setting: OrganisationSettingEnum.RecommendedRoutesBeta,
      organisationId: organisation.id,
      component: 'organisation-custom-settings',
    });

    if (organisationSettings.length === 0) return false;

    const parsedSettings = this.organisationSettingsService.parseSettings(organisationSettings);

    const routeSearchSetting = parsedSettings[OrganisationSettingEnum.RecommendedRoutesBeta];

    return !!(routeSearchSetting && routeSearchSetting.intValue === 1);
  }

  public getRouteCountByJobId(jobId: number): Promise<number> {
    return this.http.get<any>(`${this.url}/route-count-for-job/${jobId}`).toPromise();
  }

  public setDefaultRouteSearch(organisationId: number, enabled: boolean): Promise<any> {
    return this.http
      .put(`${this.url}/organisations/${organisationId}/default-route-search/${enabled}`, null)
      .toPromise();
  }

  public async isDefaultToRouteSearch(): Promise<boolean> {
    const settings = await this.organisationSettingsService.search({
      setting: OrganisationSettingEnum.DefaultToRouteSearch,
      organisationId: this.userService.currentUser.organisationId,
      component: 'organisation-custom-settings',
    });

    return settings.some((s) => s.intValue === 1);
  }

  watchedEvent = new EventEmitter<number>();

  unWatchedEvent = new EventEmitter<number>();

  hiddenEvent = new EventEmitter<number>();

  unHiddenEvent = new EventEmitter<number>();

  grabbedEvent = new EventEmitter<number>();

  refreshSearchEvent = new EventEmitter<number>();
}
